import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import platformApi from '../services/platformApi';
import PageHeader from '../components/ui/PageHeader';
import LoadingScreen from '../components/ui/LoadingScreen';
import EmptyState from '../components/ui/EmptyState';
import Modal from '../components/ui/Modal';
import { useToast } from '../contexts/ToastContext';
import { formatDate } from '../utils/billingAdmin';
import {
  EMPTY_PROMO_FORM,
  PROMO_KIND_LABELS,
  describeAppliesTo,
  describeRedemption,
  describeUsage,
  describeWindow,
  formToPayload,
  promoToForm,
  signupLink,
} from '../utils/promoCodeForm';

const STATUS_BADGE = { active: 'active', paused: 'pending', archived: 'inactive' };

function PromoStatus({ status }) {
  return <span className={`badge badge--${STATUS_BADGE[status] || 'inactive'}`}>{status}</span>;
}

function PromoCodes() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_PROMO_FORM);
  const [saving, setSaving] = useState(false);
  const [redemptions, setRedemptions] = useState(null);

  const planNames = useMemo(() => Object.fromEntries(plans.map((p) => [p.code, p.name])), [plans]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await platformApi.get('/platform/promo-codes', {
        params: { limit: 100, ...(statusFilter ? { status: statusFilter } : {}) },
      });
      setItems(response.data?.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load promo codes');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, toast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.post('/admin/plans/list', { page: 1, limit: 50, status: 'active' })
      .then((response) => {
        const rows = response.data?.data || [];
        setPlans(rows.filter((p) => p.code !== 'free' && (Number(p.priceMonthly) > 0 || Number(p.priceYearly) > 0)));
      })
      .catch(() => setPlans([]));
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(promoToForm(null));
    setShowForm(true);
  };

  const openEdit = (promo) => {
    setEditing(promo);
    setForm(promoToForm(promo));
    setShowForm(true);
  };

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setField(name, type === 'checkbox' ? checked : value);
  };
  const togglePlan = (code, on) => setForm((prev) => ({
    ...prev,
    planCodes: on ? [...new Set([...prev.planCodes, code])] : prev.planCodes.filter((c) => c !== code),
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { payload, error } = formToPayload(form, { editing: Boolean(editing) });
    if (error) {
      toast.error(error);
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await platformApi.patch(`/platform/promo-codes/${editing.id}`, payload);
        toast.success(`${editing.code} updated`);
      } else {
        await platformApi.post('/platform/promo-codes', payload);
        toast.success(`${payload.code} created`);
      }
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save promo code');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (promo, status) => {
    try {
      await platformApi.patch(`/platform/promo-codes/${promo.id}`, { status });
      toast.success(`${promo.code} ${status === 'active' ? 'resumed' : status}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update promo code');
    }
  };

  const openRedemptions = async (promo) => {
    setRedemptions({ promo, items: null });
    try {
      const response = await platformApi.get(`/platform/promo-codes/${promo.id}/redemptions`, { params: { limit: 100 } });
      setRedemptions({ promo, items: response.data?.data?.items || [], total: response.data?.data?.total || 0 });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load redemptions');
      setRedemptions(null);
    }
  };

  const copyLink = async (code) => {
    try {
      await navigator.clipboard.writeText(signupLink(code));
      toast.success('Sign-up link copied');
    } catch {
      toast.error('Could not copy the link');
    }
  };

  if (loading && items.length === 0) {
    return <LoadingScreen message="Loading promo codes..." />;
  }

  const isTrial = form.kind === 'trial';

  return (
    <div className="page">
      <PageHeader
        title="Promo codes"
        description="Discounts on one payment at checkout, and longer free trials for new sign-ups. Codes can't be combined with autopay."
        action={(
          <button type="button" className="btn btn--primary" onClick={openCreate}>
            New promo code
          </button>
        )}
      />

      <div className="card card__body" style={{ marginBottom: '1rem' }}>
        <div className="form-group" style={{ margin: 0, maxWidth: 220 }}>
          <label className="form-label" htmlFor="promoStatus">Status</label>
          <select id="promoStatus" className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="card">
          <EmptyState
            title="No promo codes yet"
            description="Create a percent or flat discount for checkout, or a longer free trial to share in a sign-up link."
            action={(
              <button type="button" className="btn btn--primary" onClick={openCreate}>
                New promo code
              </button>
            )}
          />
        </div>
      ) : (
        <div className="card">
          <div className="table-wrapper">
            <table className="data-table data-table--responsive">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Offer</th>
                  <th>Applies to</th>
                  <th>Window</th>
                  <th>Used</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((promo) => (
                  <tr key={promo.id}>
                    <td data-label="Code">
                      <strong className="cell-mono">{promo.code}</strong>
                      {promo.description ? <div className="form-hint">{promo.description}</div> : null}
                    </td>
                    <td data-label="Offer">
                      {promo.summary}
                      {promo.newCustomersOnly ? <div className="form-hint">New customers only</div> : null}
                    </td>
                    <td data-label="Applies to">{describeAppliesTo(promo, planNames)}</td>
                    <td data-label="Window">{describeWindow(promo)}</td>
                    <td data-label="Used">{describeUsage(promo)}</td>
                    <td data-label="Status"><PromoStatus status={promo.status} /></td>
                    <td data-label="Actions" className="cell-actions">
                      <div className="btn-group">
                        <button type="button" className="btn btn--secondary btn--sm" onClick={() => openEdit(promo)}>Edit</button>
                        <button type="button" className="btn btn--secondary btn--sm" onClick={() => openRedemptions(promo)}>Usage</button>
                        <button type="button" className="btn btn--secondary btn--sm" onClick={() => copyLink(promo.code)}>Copy sign-up link</button>
                        {promo.status === 'active' ? (
                          <button type="button" className="btn btn--secondary btn--sm" onClick={() => setStatus(promo, 'paused')}>Pause</button>
                        ) : null}
                        {promo.status === 'paused' ? (
                          <button type="button" className="btn btn--secondary btn--sm" onClick={() => setStatus(promo, 'active')}>Resume</button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <Modal
          title={editing ? `Edit ${editing.code}` : 'New promo code'}
          subtitle={isTrial
            ? 'Lengthens a new workspace’s free trial, counted from the day it started. Works once per workspace.'
            : 'Takes money off one checkout payment (GST is charged on the discounted price). Works once per workspace.'}
          onClose={() => setShowForm(false)}
          size="lg"
        >
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label" htmlFor="promo-code">Code</label>
                <input
                  id="promo-code"
                  name="code"
                  className="form-input cell-mono"
                  value={form.code}
                  onChange={(e) => setField('code', e.target.value.toUpperCase())}
                  disabled={Boolean(editing)}
                  required
                  maxLength={32}
                  placeholder="LAUNCH20"
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="promo-kind">Type</label>
                <select id="promo-kind" name="kind" className="form-select" value={form.kind} onChange={handleChange} disabled={Boolean(editing)}>
                  {Object.entries(PROMO_KIND_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              {form.kind === 'percent' ? (
                <div className="form-group">
                  <label className="form-label" htmlFor="promo-percent">Percent off</label>
                  <input id="promo-percent" name="percentOff" type="number" min="1" max="95" step="1" className="form-input" value={form.percentOff} onChange={handleChange} placeholder="20" />
                  <p className="form-hint">Rounded to whole rupees. Up to 95%.</p>
                </div>
              ) : null}
              {form.kind === 'flat' ? (
                <div className="form-group">
                  <label className="form-label" htmlFor="promo-amount">Amount off (₹, before GST)</label>
                  <input id="promo-amount" name="amountOffRupees" type="number" min="1" step="1" className="form-input" value={form.amountOffRupees} onChange={handleChange} placeholder="500" />
                  <p className="form-hint">The customer always pays at least ₹1.</p>
                </div>
              ) : null}
              {isTrial ? (
                <div className="form-group">
                  <label className="form-label" htmlFor="promo-trial">Trial length (days)</label>
                  <input id="promo-trial" name="trialDays" type="number" min="15" max="90" step="1" className="form-input" value={form.trialDays} onChange={handleChange} />
                  <p className="form-hint">Total trial length, 15–90 days. The standard trial is 14 days.</p>
                </div>
              ) : null}
              <div className="form-group">
                <label className="form-label" htmlFor="promo-max">Usage limit</label>
                <input id="promo-max" name="maxRedemptions" type="number" min="1" step="1" className="form-input" value={form.maxRedemptions} onChange={handleChange} placeholder="No limit" />
                <p className="form-hint">Total workspaces that can use it.</p>
              </div>
            </div>

            {!isTrial ? (
              <>
                <div className="form-group">
                  <div className="form-label">Plans</div>
                  <p className="form-hint" style={{ marginTop: 0 }}>Leave all unticked to allow every paid plan.</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                    {plans.map((plan) => (
                      <label key={plan.code} className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                        <input type="checkbox" checked={form.planCodes.includes(plan.code)} onChange={(e) => togglePlan(plan.code, e.target.checked)} />
                        {plan.name}{plan.isPublic === false ? ' (private)' : ''}
                      </label>
                    ))}
                    {!plans.length ? <span className="form-hint">Plans couldn’t be loaded; the code will apply to every paid plan.</span> : null}
                  </div>
                </div>
                <div className="form-group">
                  <div className="form-label">Billing cycles</div>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                      <input type="checkbox" name="monthly" checked={form.monthly} onChange={handleChange} /> Monthly
                    </label>
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                      <input type="checkbox" name="yearly" checked={form.yearly} onChange={handleChange} /> Yearly
                    </label>
                  </div>
                  <p className="form-hint">Yearly plans are already discounted; allow yearly only when you mean to stack.</p>
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input type="checkbox" name="newCustomersOnly" checked={form.newCustomersOnly} onChange={handleChange} />
                    New customers only (workspaces that have never paid)
                  </label>
                </div>
              </>
            ) : null}

            <div style={{ display: 'grid', gridTemplateColumns: editing ? '1fr 1fr 1fr' : '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label" htmlFor="promo-start">Starts (IST)</label>
                <input id="promo-start" name="startsOn" type="date" className="form-input" value={form.startsOn} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="promo-end">Ends (IST, inclusive)</label>
                <input id="promo-end" name="expiresOn" type="date" className="form-input" value={form.expiresOn} onChange={handleChange} />
              </div>
              {editing ? (
                <div className="form-group">
                  <label className="form-label" htmlFor="promo-status">Status</label>
                  <select id="promo-status" name="status" className="form-select" value={form.status} onChange={handleChange}>
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              ) : null}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="promo-note">Internal note</label>
              <input id="promo-note" name="description" className="form-input" value={form.description} onChange={handleChange} maxLength={200} placeholder="e.g. Diwali campaign, partner webinar" />
            </div>

            <div className="btn-group" style={{ justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button type="button" className="btn btn--secondary" onClick={() => setShowForm(false)} disabled={saving}>Cancel</button>
              <button type="submit" className="btn btn--primary" disabled={saving}>
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Create code'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {redemptions && (
        <Modal
          title={`${redemptions.promo.code} usage`}
          subtitle={redemptions.items ? `${redemptions.total} workspace${redemptions.total === 1 ? '' : 's'} used this code.` : 'Loading…'}
          onClose={() => setRedemptions(null)}
          size="lg"
        >
          {!redemptions.items ? (
            <p className="form-hint">Loading…</p>
          ) : redemptions.items.length === 0 ? (
            <p className="form-hint">Nobody has used this code yet.</p>
          ) : (
            <div className="table-wrapper">
              <table className="data-table data-table--responsive">
                <thead>
                  <tr>
                    <th>Workspace</th>
                    <th>Used on</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {redemptions.items.map((r) => (
                    <tr key={r.id}>
                      <td data-label="Workspace">{r.workspaceName || <span className="cell-mono">{r.tenantId || r.organizationId}</span>}</td>
                      <td data-label="Used on">{formatDate(r.createdAt, { withTime: true })}</td>
                      <td data-label="Details">{describeRedemption(r)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

export default PromoCodes;
